// ExpiredAccessList — the My Access "Expired" tab list (design my-access.jsx
// ExpiredAccessList). Each row shows the app breadcrumb + item name, a red
// "Expired (… ago)" status, and a Renew action that re-opens the request flow
// focused on that catalog item (the design's "renewals open the request wizard
// with the item pre-selected").

import { Box, Flex, Icon } from "@chakra-ui/react";
import { Suspense, useState } from "react";
import { TbCircleXFilled } from "react-icons/tb";

import AppLogo from "components/ds/AppLogo";
import BreadcrumbRow from "components/ds/BreadcrumbRow";
import UtilityButton from "components/ds/UtilityButton";
import { RequestAccessModal } from "components/RequestAccess";
import { type MFSelectableResourceAccess } from "state/members/access_v2/catalogs";

import { accessRowIdentity } from "./AccessGroup";

const DAY_MS = 86_400_000;

// "Expired (today)" / "(Nd ago)" / "(Nmo ago)" / "(Ny ago)" — matches the design.
const expiredLabel = (expiresAt: string): string => {
  const ms = new Date(expiresAt).getTime();
  if (Number.isNaN(ms)) return "Expired";
  const daysAgo = (Date.now() - ms) / DAY_MS;
  if (daysAgo < 1) return "Expired (today)";
  if (daysAgo < 30) return `Expired (${Math.round(daysAgo)}d ago)`;
  if (daysAgo < 365) return `Expired (${Math.round(daysAgo / 30)}mo ago)`;
  return `Expired (${Math.round(daysAgo / 365)}y ago)`;
};

const ExpiredRow = ({
  access,
  divider,
  onRenew,
  onOpen,
}: {
  access: MFSelectableResourceAccess;
  divider: boolean;
  onRenew: () => void;
  onOpen?: (access: MFSelectableResourceAccess) => void;
}) => {
  const { itemName, crumbs, logoUrl } = accessRowIdentity(access);
  const clickable = Boolean(onOpen);
  return (
    <Flex
      align="center"
      gap="lg" // 16px
      px="20px"
      py="14px"
      borderTopWidth={divider ? "1px" : undefined}
      borderTopStyle={divider ? "solid" : undefined}
      borderTopColor={divider ? "neutral.200" : undefined}
      {...(clickable
        ? {
            role: "button",
            cursor: "pointer",
            transition: "background-color 100ms ease-out",
            _hover: { bg: "neutral.50" },
            _active: { bg: "neutral.100" },
            onClick: () => onOpen!(access),
          }
        : {})}
    >
      <AppLogo size="lg" name={itemName} logoUrl={logoUrl} />

      <Box flex="1" minW={0}>
        {crumbs.length > 0 && <BreadcrumbRow segments={crumbs} />}
        <Box
          fontSize="15px"
          fontWeight={600}
          color="neutral.950"
          lineHeight="1.3"
          isTruncated
        >
          {itemName}
        </Box>
      </Box>

      <Flex
        align="center"
        gap="1xs" // 6px
        flexShrink={0}
        fontSize="smd" // 13px
        fontWeight={500}
        color="error.700"
        whiteSpace="nowrap"
      >
        <Icon as={TbCircleXFilled} boxSize="14px" color="error.500" />
        {expiredLabel(access.expiresAt)}
      </Flex>

      <Box flexShrink={0} onClick={(e) => e.stopPropagation()}>
        <UtilityButton
          size="md"
          treatment="dropshadow"
          sentiment="azurite"
          onClick={onRenew}
        >
          Renew
        </UtilityButton>
      </Box>
    </Flex>
  );
};

const ExpiredAccessList = ({
  items,
  onOpen,
}: {
  items: MFSelectableResourceAccess[];
  onOpen?: (access: MFSelectableResourceAccess) => void;
}) => {
  // catalogItemId of the row being renewed → opens the request modal focused on
  // that item (it pre-adds single-resource items, like the Catalog page does).
  const [renewItemId, setRenewItemId] = useState<string | null>(null);

  return (
    <Box mb="1xl">
      <Box
        fontFamily="body"
        fontSize="md"
        fontWeight={600}
        color="neutral.900"
        mb="md"
      >
        Expired access
      </Box>
      <Box
        bg="neutral.25"
        borderRadius="component.2xl" // 12px
        boxShadow="elevLow"
        overflow="hidden"
      >
        {items.map((a, i) => (
          <ExpiredRow
            key={a.id}
            access={a}
            divider={i > 0}
            onRenew={() => setRenewItemId(a.catalogItemId)}
            onOpen={onOpen}
          />
        ))}
      </Box>

      {/* Mount only while open so the basket resets between renewals. Wrapped in
          its own Suspense (fallback null): the modal pulls catalogs/recipients
          (suspense queries this page doesn't preload), so isolating it keeps a
          first open from blanking the whole page behind the route fallback. */}
      {renewItemId && (
        <Suspense fallback={null}>
          <RequestAccessModal
            isOpen
            onClose={() => setRenewItemId(null)}
            initialCatalogItemId={renewItemId}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default ExpiredAccessList;
